import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { ThemeProvider } from '@material-ui/core/styles';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Icon, IconButton, Tooltip } from '@material-ui/core';
import { MdFileDownload, MdFileUpload } from 'react-icons/md';
import EditFormSelectField from 'modules/ui/editform/fields/EditFormSelectField';
import UserGroupService from 'app/admin/userGroup/service/UserGroupService';

import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { showMessage } from 'app/store/fuse/messageSlice';
import EditFormGridComponent from 'modules/ui/editform/subForm/EditFormGridComponent';
import EditFormTextField from 'modules/ui/editform/fields/EditFormTextField';
import FelhasznalokService from '../service/FelhasznalokService';

function MassImport(props) {
    const mainTheme = useSelector(selectMainTheme);
    const dispatch = useDispatch();
    const [showDialog, setShowDialog] = useState(false);
    const [generatingPasswords, setGeneratingPasswords] = useState(false);

    const defaultValues = { items: [{ name: '', email: '', userGroup: '', password: '', userGroups: [] }] };

    const methods = useForm({
        defaultValues,
        mode: 'onChange'
    });

    const handleOpen = () => {
        methods.reset(defaultValues);
        setShowDialog(true);
    };

    const handleCancel = () => {
        setShowDialog(false);
    };

    const getCleanedItems = () => {
        const value = methods.getValues();
        const items = value && value.items ? value.items : [];

        const cleanedItems = items.filter((item) => {
            if (!item) return false;
            return !!(item.name || item.email || item.userGroup || item.password);
        });

        return cleanedItems;
    };

    const parseXlsTextToItems = (clipText) => {
        const lines = clipText.split(/\r?\n/).map((line) => line.trim()).filter((line) => line !== '');

        const items = [];

        for (let i = 0; i < lines.length; i += 1) {
            const line = lines[i];
            if (!line) continue;

            const cols = line.split(/\t|;/);

            const userGroup = cols[0] ? cols[0].trim() : '';
            const name = cols[2] ? cols[2].trim() : '';
            const email = cols[3] ? cols[3].trim() : '';
            const password = ''; // jelszó egyelőre üres, később API tölti

            if (!userGroup && !name && !email) continue;

            items.push({ name, email, userGroup, password, userGroups: [] });
        }

        return items;
    };

    const handlePasteXls = () => {
        if (!navigator.clipboard) {
            dispatch(showMessage({ message: 'Hiba: a vágólap nem érhető el.' }));
            return;
        }

        navigator.clipboard
            .readText()
            .then((clipText) => {
                if (!clipText || clipText === '') {
                    dispatch(showMessage({ message: 'Sikertelen importálás a vágólapról.' }));
                    return;
                }

                const items = parseXlsTextToItems(clipText);

                if (!items.length) {
                    dispatch(showMessage({ message: 'Sikertelen importálás a vágólapról.' }));
                    return;
                }

                methods.setValue('items', items);

                dispatch(showMessage({ message: 'Sikeres importálás a vágólapról.' }));
            })
            .catch((err) => {
                console.error('Failed to read clipboard contents: ', err);
                dispatch(showMessage({ message: 'Sikertelen importálás a vágólapról.' }));
            });
    };

    const handleCopyXls = () => {
        const items = getCleanedItems();

        if (!items.length) {
            dispatch(showMessage({ message: 'Nincs exportálható sor.' }));
            return;
        }

        UserGroupService.getListSelection()
            .then((userGroupsSelection) => {
                const userGroupMap = {};

                if (Array.isArray(userGroupsSelection)) {
                    for (let i = 0; i < userGroupsSelection.length; i += 1) {
                        const g = userGroupsSelection[i];
                        if (!g) continue;

                        const id = g.id != null ? g.id : g.value;
                        const name = g.name || g.label || '';

                        if (id != null && name) {
                            userGroupMap[id] = name;
                        }
                    }
                }

                const rows = [];

                rows.push('Név\tEmail cím\tFejezet\tJelszó\tFelhasználó csoport');

                for (let i = 0; i < items.length; i += 1) {
                    const item = items[i];
                    if (!item) continue;

                    const name = item.name || '';
                    const email = item.email || '';
                    const userGroup = item.userGroup || '';
                    const password = item.password || '';

                    let userGroupsLabel = '';

                    if (Array.isArray(item.userGroups) && item.userGroups.length > 0) {
                        const labels = item.userGroups.map((ug) => {
                            if (!ug) return null;

                            if (typeof ug === 'object') {
                                if (ug.name) return ug.name;
                                if (ug.label) return ug.label;
                                if (ug.id != null && userGroupMap[ug.id]) return userGroupMap[ug.id];
                                if (ug.value != null && userGroupMap[ug.value]) return userGroupMap[ug.value];
                                return null;
                            }

                            if (userGroupMap[ug]) return userGroupMap[ug];

                            return null;
                        }).filter((v) => !!v);

                        userGroupsLabel = labels.join(', ');
                    }

                    const line = name + '\t' + email + '\t' + userGroup + '\t' + password + '\t' + userGroupsLabel;

                    rows.push(line);
                }

                const cData = rows.join('\n');

                if (navigator.clipboard) {
                    navigator.clipboard
                        .writeText(cData)
                        .then(() => {
                            dispatch(showMessage({ message: 'Sikeres exportálás a vágólapra.' }));
                        })
                        .catch((err) => {
                            console.error('Failed to write clipboard contents: ', err);
                            dispatch(showMessage({ message: 'Sikertelen exportálás a vágólapra.' }));
                        });
                } else {
                    dispatch(showMessage({ message: 'Hiba: a vágólap nem érhető el.' }));
                }
            })
            .catch((err) => {
                console.error('Failed to load user group selection: ', err);
                dispatch(showMessage({ message: 'Sikertelen exportálás a vágólapra.' }));
            });
    };

    const handleGeneratePasswords = () => {
        const value = methods.getValues();
        const items = value && value.items ? value.items : [];

        if (!items.length) {
            dispatch(showMessage({ message: 'Nincs sor a jelszavak generálásához.' }));
            return;
        }

        const count = items.length;

        setGeneratingPasswords(true);

        FelhasznalokService.generatePasswords(count).then((passwords) => {
            if (!Array.isArray(passwords) || !passwords.length) {
                dispatch(showMessage({ message: 'Hiba: a jelszavak nem generálódtak le megfelelően.' }));
                return;
            }

            if (passwords.length < count) {
                console.warn('Kevesebb jelszó érkezett a vártnál:', passwords.length, '<', count);
            }

            const currentItems = methods.getValues('items') || [];

            const updatedItems = currentItems.map((item, index) => {
                if (!item) return item;
                const generatedPassword = index < passwords.length ? passwords[index] : null;
                return { ...item, password: generatedPassword != null ? generatedPassword : item.password };
            });

            methods.setValue('items', updatedItems);

            dispatch(showMessage({ message: 'Jelszavak sikeresen generálva.' }));
        }).catch((error) => {
            console.error('generatePasswords error', error);
            dispatch(showMessage({ message: 'Hiba történt a jelszavak generálása közben.' }));
        }).finally(() => { setGeneratingPasswords(false); });
    };

    const handleClear = () => {
        methods.reset(defaultValues);
    };

    const handleSend = () => {
        const items = getCleanedItems();

        if (!items.length) {
            dispatch(showMessage({ message: 'Nincs elküldhető sor.' }));
            return;
        }

        const reviewerUsers = items.map((item) => {
            return {
                email: item.email || null,
                name: item.name || null,
                password: item.password || null,
                userGroup: item.userGroup || null // egyelőre stringként küldjük
            };
        });

        console.log('[MassImport][send][reviewerUsers]', reviewerUsers);

        FelhasznalokService.storeReviewerUsers(reviewerUsers).then((response) => {
            console.log('[MassImport][send][response]', response);
            dispatch(showMessage({ message: 'Felhasználók sikeresen mentve.' }));

            if (props.onSubmit) props.onSubmit(items);

            setShowDialog(false);
        }).catch((error) => {
            console.error('storeReviewerUsers error', error);
            dispatch(showMessage({ message: 'Hiba történt a felhasználók mentése közben.' }));
        });
    };

    return (
        <>
            <Button className="whitespace-nowrap mx-4" variant="contained" color="secondary" onClick={handleOpen} startIcon={<Icon className="hidden sm:flex">add</Icon>}>Tömeges importálás</Button>

            <ThemeProvider theme={mainTheme}>
                <Dialog open={showDialog} fullWidth maxWidth="lg" onClose={handleCancel}>

                    <DialogTitle disableTypography className="border-b">Tömeges importálás</DialogTitle>

                    <DialogContent className="w-full p-20">
                        <FormProvider {...methods}>
                            <EditFormGridComponent
                                config={{ topic: 'mass_import' }}
                                fieldConfig={{
                                    key: 'items',
                                    component: EditFormGridComponent,
                                    defaultValue: {
                                        name: '',
                                        email: '',
                                        userGroup: '',
                                        password: '',
                                        userGroups: []
                                    },
                                    fields: [
                                        {
                                            key: 'name',
                                            text: 'Név',
                                            component: EditFormTextField,
                                            required: true
                                        },
                                        {
                                            key: 'email',
                                            text: 'Email cím',
                                            component: EditFormTextField,
                                            required: true
                                        },
                                        {
                                            key: 'password',
                                            text: 'Jelszó',
                                            component: EditFormTextField,
                                            required: true
                                        },
                                        {
                                            key: 'userGroup',
                                            text: 'Felhasználó csoport',
                                            component: EditFormSelectField,
                                            // multiple: true,
                                            required: true,
                                            items: () => UserGroupService.getListSelection()
                                        }
                                    ]
                                }}
                            />
                        </FormProvider>
                    </DialogContent>

                    <DialogActions className="justify-between border-t p-20">
                        <Button onClick={handleCancel}>Vissza</Button>

                        <div className="flex items-center gap-8">
                            <Button onClick={handleClear}>Adatok törlése</Button>
                            <Button onClick={handleGeneratePasswords} disabled={generatingPasswords}>Jelszavak generálása</Button>
                            <Tooltip title="Importálás vágólapról (Excel)">
                                <Button color="secondary" variant="contained" onClick={handlePasteXls} startIcon={<MdFileDownload className="text-lg" />}>Importálás</Button>
                            </Tooltip>
                            <Tooltip title="Exportálás vágólapra (Excel)">
                                <Button color="secondary" variant="contained" onClick={handleCopyXls} startIcon={<MdFileUpload className="text-lg" />}>Exportálás</Button>
                            </Tooltip>
                            <Button color="primary" variant="contained" onClick={handleSend}>Küldés</Button>
                        </div>
                    </DialogActions>
                </Dialog>
            </ThemeProvider>
        </>
    );
}

export default MassImport;